import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import { rmApi } from '../../services/api';
import { FaSpinner, FaExclamationTriangle, FaSearch, FaFilter, FaTimes, FaCheck, FaTimes as FaTimesClose, FaEye } from 'react-icons/fa'; // Removed FaEye, FaPrint
import Pagination from '../../components/Pagination';
import Select from 'react-select';
import { Container, Card, Row, Col, Table, Button, Form } from 'react-bootstrap'; // Removed Modal, InputGroup
import { useNavigate } from 'react-router-dom';
// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch (e) {
    return dateString; // Fallback
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function RMInvoices() {

  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Selection state
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [entriesPerPage] = useState(10);
  const [actionLoading, setActionLoading] = useState(null);

  // Filter state
  const [dsoOptions, setDsoOptions] = useState([]);
  const [selectedDso, setSelectedDso] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [filterApplied, setFilterApplied] = useState(false);

  // Status filter options
  const approvalStatusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  const paymentStatusOptions = [
    { value: 'Generated', label: 'Generated' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Failed', label: 'Failed' }
  ];

  // Fetch DSOs for filter dropdown
  const fetchDsoOptions = useCallback(async () => {
    try {
      // Fetch all DSOs assigned to current RM (use a high limit to get all)
      const response = await rmApi.getAssignedDsos({ limit: 1000 });
      const options = response.data.items.map(dso => ({
        value: dso.id,
        label: dso.name
      }));
      setDsoOptions(options);
    } catch (err) {
      console.error("Error fetching DSO options:", err);
    }
  }, []);

  // Fetch invoices function with filters
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = (currentPage - 1) * entriesPerPage;
      
      // Build filter params
      const params = {
        skip,
        limit: entriesPerPage
      };
      
      // Add filters if they're selected
      if (selectedDso) {
        params.dso_id = selectedDso.value;
      }
      
      if (invoiceNumber.trim()) {
        params.invoice_number = invoiceNumber.trim();
      }
      
      if (approvalStatus) {
        params.approval_status = approvalStatus.value;
      }
      
      if (paymentStatus) {
        params.payment_status = paymentStatus.value;
      }
      
      // Set flag if any filter is applied
      setFilterApplied(
        !!selectedDso || 
        !!invoiceNumber.trim() || 
        !!approvalStatus || 
        !!paymentStatus
      );
      
      const response = await rmApi.getInvoices(params);
      setInvoices(response.data.items || []);
      setTotalInvoices(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / entriesPerPage));
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError('Failed to load invoices. Please try again.');
      // Reset state on error
      setInvoices([]);
      setTotalInvoices(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, selectedDso, invoiceNumber, approvalStatus, paymentStatus]);

  // Load DSO options on first render
  useEffect(() => {
    fetchDsoOptions();
  }, [fetchDsoOptions]);

  // Initial fetch and fetch on page change or filters change
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Handle page change from Pagination component
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Reset selection when changing pages
    setSelectedInvoices([]);
    setSelectAll(false);
  };
  
  // Handle checkbox selection for a single invoice
  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      let newSelected;
      // If already selected, remove it
      if (prev.includes(invoiceId)) {
        newSelected = prev.filter(id => id !== invoiceId);
      } 
      // Otherwise add it
      else {
        newSelected = [...prev, invoiceId];
      }
      
      // Check if all selectable invoices are selected to update selectAll state
      const selectableInvoices = invoices
        .filter(invoice => invoice.invoice_status_str?.toLowerCase() === 'pending')
        .map(invoice => invoice.id);
      
      // If all selectable invoices are selected, set selectAll to true
      if (selectableInvoices.length > 0 && 
          selectableInvoices.every(id => newSelected.includes(id))) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
      
      return newSelected;
    });
  };
  
  // Handle select all checkbox
  const handleSelectAllChange = () => {
    if (selectAll) {
      // If currently all selected, unselect all
      setSelectedInvoices([]);
    } else {
      // Otherwise select all invoices that can be acted upon (pending ones)
      const pendingInvoices = invoices
        .filter(invoice => invoice.invoice_status_str?.toLowerCase() === 'pending')
        .map(invoice => invoice.id);
      setSelectedInvoices(pendingInvoices);
    }
    setSelectAll(!selectAll);
  };
  
  // Bulk approve selected invoices
  const handleBulkApprove = async () => {
    if (selectedInvoices.length === 0) return;
    
    setBulkActionLoading(true);
    setError('');
    
    try {
      // Process each selected invoice
      const promises = selectedInvoices.map(invoiceId => 
        rmApi.updateInvoiceStatus(invoiceId, 'Approved')
      );
      
      await Promise.all(promises);
      
      // Refresh data after successful update
      fetchInvoices();
      // Clear selection
      setSelectedInvoices([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error bulk approving invoices:", err);
      setError('Failed to approve some invoices. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Bulk reject selected invoices
  const handleBulkReject = async () => {
    if (selectedInvoices.length === 0) return;
    
    setBulkActionLoading(true);
    setError('');
    
    try {
      // Process each selected invoice
      const promises = selectedInvoices.map(invoiceId => 
        rmApi.updateInvoiceStatus(invoiceId, 'Rejected')
      );
      
      await Promise.all(promises);
      
      // Refresh data after successful update
      fetchInvoices();
      // Clear selection
      setSelectedInvoices([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error bulk rejecting invoices:", err);
      setError('Failed to reject some invoices. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Function to render approval status badge
  const renderApprovalStatusBadge = (status) => {
    let badgeClass = 'rm-status-badge ';
    switch (status?.toLowerCase()) {
      case 'approved':
        badgeClass += 'rm-status-approved'; // Green
        break;
      case 'rejected':
        badgeClass += 'rm-status-rejected'; // Red
        break;
      case 'pending': // Assuming 'Pending' is the initial state
      default:
        badgeClass += 'rm-status-pending'; // Yellow/Orange
    }
    return <span className={badgeClass}>{status || 'Pending'}</span>;
  };

  // Function to render payment status badge
  const renderPaymentStatusBadge = (status) => {
    let badgeClass = 'rm-status-badge ';
    switch (status?.toLowerCase()) {
      case 'completed':
        badgeClass += 'rm-payment-status-completed'; // Green
        break;
      case 'failed':
        badgeClass += 'rm-payment-status-failed'; // Red
        break;
      case 'pending': // Initial state
      case 'generated':
      default:
        badgeClass += 'rm-payment-status-generated'; // Blue
    }
    return <span className={badgeClass}>{status || 'Pending'}</span>;
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchInvoices();
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedDso(null);
    setInvoiceNumber('');
    setApprovalStatus(null);
    setPaymentStatus(null);
    setCurrentPage(1);
  };

  // Action Handlers
  const handleApprove = async (invoiceId) => {
    setActionLoading(invoiceId);
    setError(''); // Clear previous errors
    try {
      await rmApi.updateInvoiceStatus(invoiceId, 'Approved');
      // Refresh data after successful update
      fetchInvoices(); // fetchInvoices already sets loading to false
    } catch (err) {
      console.error("Error approving invoice:", err);
      setError(`Failed to approve invoice ${invoiceId}. Please try again.`);
      setActionLoading(null); // Ensure loading state is reset on error
    }
  };

  const handleReject = async (invoiceId) => {
    setActionLoading(invoiceId);
    setError(''); // Clear previous errors
    try {
      await rmApi.updateInvoiceStatus(invoiceId, 'Rejected');
      // Refresh data after successful update
      fetchInvoices(); // fetchInvoices already sets loading to false
    } catch (err) {
      console.error("Error rejecting invoice:", err);
      setError(`Failed to reject invoice ${invoiceId}. Please try again.`);
      setActionLoading(null); // Ensure loading state is reset on error
    }
  };

  // Payment Status Action Handlers
  const handlePaymentCompleted = async (invoiceId) => {
    setActionLoading(invoiceId);
    setError(''); // Clear previous errors
    try {
      await rmApi.updateInvoicePaymentStatus(invoiceId, 1); // 1 = Payment Completed
      // Refresh data after successful update
      fetchInvoices();
    } catch (err) {
      console.error("Error marking payment as completed:", err);
      setError(`Failed to update payment status for invoice ${invoiceId}. Please try again.`);
      setActionLoading(null);
    }
  };

  const handlePaymentFailed = async (invoiceId) => {
    setActionLoading(invoiceId);
    setError(''); // Clear previous errors
    try {
      await rmApi.updateInvoicePaymentStatus(invoiceId, 2); // 2 = Payment Failed
      // Refresh data after successful update
      fetchInvoices();
    } catch (err) {
      console.error("Error marking payment as failed:", err);
      setError(`Failed to update payment status for invoice ${invoiceId}. Please try again.`);
      setActionLoading(null);
    }
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '36px',
      height: '36px',
      boxShadow: 'none',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.85rem',
      '&:hover': {
        border: '1px solid #cbd5e1',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '36px',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    option: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
      padding: '6px 12px',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
      color: '#94a3b8',
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
    }),
  };

  return (
    <div className="rm-invoices-page rm-list-page">
      <div className="rm-list-header">
        <h2>DSO Invoices</h2>
        {filterApplied && (
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="clear-filters-btn"
            onClick={clearFilters}
          >
            <FaTimes /> Clear Filters
          </Button>
        )}
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <Form onSubmit={handleFilterSubmit}>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>DSO</Form.Label>
                <Select
                  value={selectedDso}
                  onChange={setSelectedDso}
                  options={dsoOptions}
                  isClearable
                  placeholder="Select DSO"
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Search invoice #"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Approval Status</Form.Label>
                <Select
                  value={approvalStatus}
                  onChange={setApprovalStatus}
                  options={approvalStatusOptions}
                  isClearable
                  placeholder="Select status"
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Payment Status</Form.Label>
                <Select
                  value={paymentStatus}
                  onChange={setPaymentStatus}
                  options={paymentStatusOptions}
                  isClearable
                  placeholder="Select status"
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button 
                type="submit" 
                variant="primary" 
                className="w-100"
              >
                <FaSearch /> Filter
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {loading && (
        <div className="rm-loading-state">
          <FaSpinner className="rm-spinner" />
          <span>Loading invoices...</span>
        </div>
      )}

      {error && (
        <div className="rm-error-state">
          <FaExclamationTriangle className="rm-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="rm-no-data">No invoices found.</div>
      )}

      {/* Bulk Action Buttons - show only when items are selected */}
      {selectedInvoices.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="selected-count">
            {selectedInvoices.length} {selectedInvoices.length === 1 ? 'invoice' : 'invoices'} selected
          </span>
          <div className="bulk-action-buttons">
            <button
              className="bulk-action-button bulk-approve"
              onClick={handleBulkApprove}
              disabled={bulkActionLoading}
              title="Approve all selected invoices"
            >
              {bulkActionLoading ? <FaSpinner className="spin-icon" /> : <FaCheck />} 
              Bulk Approve
            </button>
            <button
              className="bulk-action-button bulk-reject"
              onClick={handleBulkReject}
              disabled={bulkActionLoading}
              title="Disapprove all selected invoices"
            >
              {bulkActionLoading ? <FaSpinner className="spin-icon" /> : <FaTimesClose />} 
              Bulk Disapprove
            </button>
          </div>
        </div>
      )}
      
      {!loading && !error && invoices.length > 0 && (
        <>
          <div className="rm-list-table-container">
            <table className="rm-list-table">
              <thead>
                <tr>
                  <th className="select-column">
                    <div className="select-header">
                      <input
                        type="checkbox"
                        className="select-checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        title="Select all invoices"
                      />
                    </div>
                  </th>
                  <th>Invoice #</th>
                  <th>Date Generated</th>
                  <th>DSO Name</th>
                  <th>Amount</th>
                  <th>Case Count</th>
                  <th>Approval Status</th>
                  <th>Payment Status</th>
                  <th>Payment Date</th>
                  <th>Options</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr 
                    key={invoice.id} 
                    className={selectedInvoices.includes(invoice.id) ? 'selected-row' : ''}
                  >
                    <td className="select-column">
                      <input
                        type="checkbox"
                        className="select-checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleSelectInvoice(invoice.id)}
                        // Only allow selection of invoices that can be acted upon
                        disabled={invoice.invoice_status_str?.toLowerCase() !== 'pending'}
                      />
                    </td>
                    <td>
                      <Link
                        to={`/rm/invoices/${invoice.id}/details`} // Link to the details page
                        className="invoice-link"
                        title="View invoice details"
                      >
                        {invoice.invoice_no || 'N/A'}
                      </Link>
                    </td>
                    <td>{formatDate(invoice.created_at)}</td>
                    <td>{invoice.dso_name}</td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>{invoice.case_count}</td>
                    {/* Approval status */}
                    <td>{renderApprovalStatusBadge(invoice.invoice_status_str)}</td>
                    {/* Payment status */}
                    <td>{renderPaymentStatusBadge(invoice.payment_status_str)}</td>
                    <td>{formatDate(invoice.payment_date)}</td>
                    <td>
                      <div className="rm-action-buttons">
                        {/* Removed View (FaEye) button */}
                        <button
                          onClick={() => navigate(`/rm/invoices/${invoice.id}/details`)}
                          className="rm-icon-button rm-button-view"
                          title="View Invoice"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleApprove(invoice.id)}
                          disabled={actionLoading === invoice.id || invoice.invoice_status_str?.toLowerCase() === 'approved'}
                          className="rm-icon-button rm-button-approve"
                          title="Approve invoice"
                        >
                          <FaCheck />
                        </button>
                        <button
                          onClick={() => handleReject(invoice.id)}
                          disabled={actionLoading === invoice.id || invoice.invoice_status_str?.toLowerCase() === 'rejected'}
                          className="rm-icon-button rm-button-reject"
                          title="Disapprove invoice"
                        >
                          <FaTimesClose />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsCount={totalInvoices}
            itemsPerPage={entriesPerPage}
          />
        </>
      )}

      {/* Add common list page styles here or import from a CSS file */}
      <style jsx>{`
        .rm-list-page {
          padding: 12px;
          background-color: #f8fafc;
          max-width: 100%;
          overflow-x: hidden;
        }
        .rm-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e2e8f0;
        }
        .rm-list-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
          color: #1e293b;
        }
        .filter-section {
          background-color: #ffffff;
          padding: 12px 12px 6px;
          margin-bottom: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        .filter-section .row {
          margin-bottom: 0 !important;
        }
        .filter-section .form-group {
          margin-bottom: 8px;
        }
        .filter-section .form-label {
          margin-bottom: 4px;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .filter-section .form-control {
          height: 36px;
          font-size: 0.85rem;
        }
        .filter-section button {
          height: 36px;
          padding: 0 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .clear-filters-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.8rem;
          padding: 3px 8px;
        }
        .rm-list-table-container {
          background-color: #ffffff;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          overflow-x: auto;
          margin-bottom: 8px;
        }
        .rm-list-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .rm-list-table th {
          text-align: left;
          padding: 8px 10px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #64748b;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          height: 36px;
          white-space: nowrap;
        }
        .rm-list-table td {
          padding: 6px 10px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: middle;
          height: 40px;
        }
        .rm-list-table tr:last-child td {
          border-bottom: none;
        }
        .rm-list-table tr:hover {
          background-color: #f8fafc;
        }
        .rm-list-table td:first-child,
        .rm-list-table th:first-child {
          padding-left: 16px;
        }
        .rm-list-table td:last-child,
        .rm-list-table th:last-child {
          padding-right: 16px;
        }

        /* Loading / Error / No Data States */
        .rm-loading-state,
        .rm-error-state,
        .rm-no-data {
          padding: 28px 0;
          text-align: center;
          color: #64748b;
          font-size: 0.9rem;
          background-color: #fff;
          border-radius: 6px;
          margin-top: 12px;
        }
        .rm-loading-state,
        .rm-error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .rm-spinner {
          font-size: 1.5rem;
          color: #6366f1;
          animation: spin 1s linear infinite;
        }
        .rm-error-icon {
          font-size: 1.5rem;
          color: #ef4444;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Status Badges */
        .rm-status-badge {
          display: inline-block;
          padding: 4px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 3px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          text-transform: capitalize;
        }
        .rm-status-approved { 
          background-color: #10b981;
          background-image: linear-gradient(to bottom, #10b981, #059669);
        }
        .rm-status-pending { 
          background-color: #f59e0b;
          background-image: linear-gradient(to bottom, #f59e0b, #d97706);
        }
        .rm-status-rejected { 
          background-color: #ef4444;
          background-image: linear-gradient(to bottom, #ef4444, #dc2626);
        }
        .rm-payment-status-completed { 
          background-color: #10b981;
          background-image: linear-gradient(to bottom, #10b981, #059669);
        }
        .rm-payment-status-failed { 
          background-color: #ef4444;
          background-image: linear-gradient(to bottom, #ef4444, #dc2626);
        }
        .rm-payment-status-generated { 
          background-color: #3b82f6;
          background-image: linear-gradient(to bottom, #3b82f6, #2563eb);
        }
        .rm-status-unknown { background-color: #94a3b8; }

        /* Action Buttons */
        .rm-action-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: nowrap;
          justify-content: flex-start;
        }
        /* Icon Buttons */
        .rm-icon-button {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          padding: 0;
          position: relative;
        }
        .rm-icon-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          box-shadow: none;
        }
        .rm-icon-button:active:not(:disabled) {
          transform: scale(0.95);
        }
        
        /* View Button (Blue) */
        .rm-button-view {
          background: linear-gradient(to bottom, #60a5fa, #3b82f6);
          color: #fff;
        }
        .rm-button-view:hover:not(:disabled) {
          background: linear-gradient(to bottom, #3b82f6, #2563eb);
          box-shadow: 0 2px 4px rgba(59,130,246,0.3);
        }
        
        /* Approve Button (Green) */
        .rm-button-approve {
          background: linear-gradient(to bottom, #34d399, #10b981);
          color: #fff;
        }
        .rm-button-approve:hover:not(:disabled) {
          background: linear-gradient(to bottom, #10b981, #059669);
          box-shadow: 0 2px 4px rgba(16,185,129,0.3);
        }
        
        /* Reject Button (Red) */
        .rm-button-reject {
          background: linear-gradient(to bottom, #f87171, #ef4444);
          color: #fff;
        }
        .rm-button-reject:hover:not(:disabled) {
          background: linear-gradient(to bottom, #ef4444, #dc2626);
          box-shadow: 0 2px 4px rgba(239,68,68,0.3);
        }
        
        /* Loading state for buttons */
        .rm-icon-button.loading {
          pointer-events: none;
        }
        .rm-icon-button.loading:after {
          content: '';
          position: absolute;
          width: 70%;
          height: 70%;
          border: 2px solid rgba(255,255,255,0.2);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 0.8s linear infinite;
        }
        
        /* Invoice link styles */
        .invoice-link {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
          font-weight: 500;
        }
        .invoice-link:hover {
          color: #2563eb;
        }
        
        /* Selection and checkboxes */
        .select-column {
          width: 40px;
          text-align: center;
          padding: 0 !important;
          vertical-align: middle;
        }
        
        .select-header {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        }
        
        .select-checkbox {
          width: 16px;
          height: 16px;
          cursor: pointer;
          border-radius: 3px;
          border: 2px solid #cbd5e1;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-color: #fff;
          margin: 0;
          position: relative;
        }
        
        .select-checkbox:checked {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }
        
        .select-checkbox:checked::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 5px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        
        .select-checkbox:disabled {
          background-color: #f1f5f9;
          border-color: #cbd5e1;
          cursor: not-allowed;
        }
        
        /* Highlight selected rows */
        .selected-row {
          background-color: rgba(59, 130, 246, 0.05) !important;
        }
        
        .selected-row:hover {
          background-color: rgba(59, 130, 246, 0.1) !important;
        }
        
        /* Bulk action bar */
        .bulk-actions-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 6px;
          padding: 10px 16px;
          margin-bottom: 10px;
        }
        
        .selected-count {
          font-size: 0.9rem;
          font-weight: 500;
          color: #0369a1;
        }
        
        .bulk-action-buttons {
          display: flex;
          gap: 10px;
        }
        
        .bulk-action-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 4px;
          border: 1px solid transparent;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .bulk-approve {
          background-color: #dcfce7;
          color: #166534;
          border-color: #bbf7d0;
        }
        
        .bulk-approve:hover:not(:disabled) {
          background-color: #bbf7d0;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .bulk-reject {
          background-color: #fee2e2;
          color: #991b1b;
          border-color: #fecaca;
        }
        
        .bulk-reject:hover:not(:disabled) {
          background-color: #fecaca;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .bulk-action-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .spin-icon {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}

export default RMInvoices;
